import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../../../components/ui/button';
import FuzzySearchInput from '../../../components/common/FuzzySearchInput';
import coverageApiService from '../../../services/coverage-api.service';
import { sendToKinesis } from '../../../analytics/kinesisTransport';
import { useSelector } from 'react-redux';

/**
 * InputBarSection Component
 * Integrated search interface for plan and drug selection (no Card wrapper)
 * Designed to fit within UnifiedCoverageContainer
 * Supports initial values to restore previous search state
 * 
 * Now using FuzzySearchInput for autocomplete prevention and validation
 */
export const InputBarSection = ({ 
  onSearch,
  initialPlanId = '',
  initialDrugId = '',
  initialPlanName = '',
  initialDrugName = '',
  initialDrugMetadata = null,
}) => {
  // Simplified state - only track selected options
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedDrug, setSelectedDrug] = useState(null);

  // Get user info for analytics
  const userAttributes = useSelector((state) => state.auth.userAttributes);

  // Prepare initial values for FuzzySearchInput components
  const initialPlan = useMemo(() => {
    if (initialPlanId && initialPlanName) {
      return {
        id: initialPlanId,
        label: initialPlanName,
        metadata: {},
      };
    }
    return null;
  }, [initialPlanId, initialPlanName]);

  const initialDrug = useMemo(() => {
    if (initialDrugId && initialDrugName) {
      return {
        id: initialDrugId,
        label: initialDrugName,
        metadata: initialDrugMetadata || {},
      };
    }
    return null;
  }, [initialDrugId, initialDrugName, initialDrugMetadata]);

  // Initialize selected values from initial props
  useEffect(() => {
    if (initialPlan) setSelectedPlan(initialPlan);
    if (initialDrug) setSelectedDrug(initialDrug);
  }, [initialPlan, initialDrug]);

  /**
   * Search function for plans
   */
  const searchPlans = async (query, signal) => {
    const response = await coverageApiService.searchPlans(query);
    return response?.plans || [];
  };

  /**
   * Map plan API result to FuzzySearchInput option format
   */
  const mapPlanToOption = (plan) => {
    // Build subtitle with labeled payer, PBM, and location
    const parts = [];
    
    if (plan.payer) parts.push(`Payer: ${plan.payer}`);
    if (plan.pbm) parts.push(`PBM: ${plan.pbm}`);
    
    // Add location if available
    if (plan.address_city && plan.address_stateid) {
      parts.push(`${plan.address_city}, ${plan.address_stateid}`);
    }
    
    // Fallback to controller if no payer/PBM
    if (parts.length === 0 && plan.controller) {
      parts.push(plan.controller);
      if (plan.address_city && plan.address_stateid) {
        parts.push(`${plan.address_city}, ${plan.address_stateid}`);
      }
    }
    
    // Final fallback
    const subtitle = parts.length > 0 ? parts.join(' • ') : `Plan ID: ${plan.planid}`;
    
    return {
      id: plan.planid.toString(),
      label: plan.name,
      subtitle: subtitle,
      metadata: {
        controller: plan.controller || '',
        payer: plan.payer || '',
        pbm: plan.pbm || '',
        ...plan,
      },
    };
  };

  /**
   * Search function for drugs
   */
  const searchDrugs = async (query, signal) => {
    const response = await coverageApiService.searchDrugs(query, 20, signal);
    return response?.products || [];
  };

  /**
   * Map drug API result to FuzzySearchInput option format
   */
  const mapDrugToOption = (drug) => ({
    id: drug.productid.toString(),
    label: drug.drug_description,
    metadata: drug,
  });

  /**
   * Handle plan selection with analytics
   */
  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    
    if (plan) {
      // Track plan selection
      try {
        sendToKinesis('coverage_events', 'COVERAGE_VERIFICATION_PLAN_SELECTED', {
          userid: userAttributes?.sub || 'anonymous',
          organizationid: userAttributes?.['custom:org_owner_sub'] || userAttributes?.sub || 'anonymous',
          planId: plan.id,
          planName: plan.label,
          payer: plan.metadata?.payer || '',
          pbm: plan.metadata?.pbm || '',
          controller: plan.metadata?.controller || '',
          state: plan.metadata?.address_stateid || '',
          city: plan.metadata?.address_city || ''
        });
      } catch (analyticsError) {
        console.error('Analytics error (non-blocking):', analyticsError);
      }
    }
  };

  /**
   * Handle drug selection with analytics
   */
  const handleDrugSelect = (drug) => {
    setSelectedDrug(drug);
    
    if (drug) {
      // Track drug selection
      try {
        sendToKinesis('coverage_events', 'COVERAGE_VERIFICATION_DRUG_SELECTED', {
          userid: userAttributes?.sub || 'anonymous',
          organizationid: userAttributes?.['custom:org_owner_sub'] || userAttributes?.sub || 'anonymous',
          drugId: drug.id,
          drugName: drug.label,
          drugDoseForm: drug.metadata?.drug_doseform || '',
          therapeuticClass: drug.metadata?.therapeuticclass || '',
          therapeuticClassId: drug.metadata?.therapeuticclassid ? Number(drug.metadata.therapeuticclassid) : null
        });
      } catch (analyticsError) {
        console.error('Analytics error (non-blocking):', analyticsError);
      }
    }
  };

  /**
   * Handle search submission
   */
  const handleSearch = () => {
    if (!isReadyToSearch) return;

    onSearch({
      planId: selectedPlan.id,
      drugId: selectedDrug.id,
      planName: selectedPlan.label,
      payer: selectedPlan.metadata?.payer || '',
      pbm: selectedPlan.metadata?.pbm || '',
      controller: selectedPlan.metadata.controller || '',
      drugMetadata: selectedDrug.metadata
        ? {
            drug_name: selectedDrug.metadata.drug_name || '',
            drug_description: selectedDrug.metadata.drug_description || '',
            drug_doseform: selectedDrug.metadata.drug_doseform,
            therapeuticclass: selectedDrug.metadata.therapeuticclass,
            therapeuticclass_id: selectedDrug.metadata.therapeuticclassid
              ? Number(selectedDrug.metadata.therapeuticclassid)
              : undefined,
            drug_medid: selectedDrug.metadata.drug_medid || null,
          }
        : undefined,
    });
  };

  const isReadyToSearch = Boolean(selectedPlan && selectedDrug);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && isReadyToSearch) {
      handleSearch();
    }
  };

  return (
    <div role="search" aria-label="Coverage verification search">
      <div
        className="flex flex-col md:flex-row gap-4 md:items-end"
        onKeyPress={handleKeyPress}
      >
        {/* Plan Name - Using FuzzySearchInput */}
        <FuzzySearchInput
          id="plan-name"
          label="Plan Name"
          placeholder="Type to search plans..."
          required
          searchFn={searchPlans}
          mapResultToOption={mapPlanToOption}
          value={selectedPlan}
          onChange={handlePlanSelect}
          initialValue={initialPlan}
          minSearchLength={1}
          debounceMs={300}
          className="flex-1"
          emptyMessage="No matching plans found—try different search terms"
          errorMessage="Please select a plan from the list"
        />

        {/* Drug Name - Using FuzzySearchInput */}
        <FuzzySearchInput
          id="drug-name"
          label="Drug Name"
          placeholder="Type to search drugs..."
          required
          searchFn={searchDrugs}
          mapResultToOption={mapDrugToOption}
          value={selectedDrug}
          onChange={handleDrugSelect}
          initialValue={initialDrug}
          minSearchLength={2}
          debounceMs={200}
          className="flex-1"
          emptyMessage="No matching drugs found—try different search terms"
          errorMessage="Please select a drug from the list"
        />

        {/* Search Button - Inline */}
        <div className="w-full md:w-auto">
          <Button
            onClick={handleSearch}
            disabled={!isReadyToSearch}
            className="w-full md:w-auto min-h-[44px] px-6 md:px-8 font-semibold shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            aria-label="Check coverage for selected plan and drug"
            aria-disabled={!isReadyToSearch}
          >
            Check Coverage
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InputBarSection;
